<template>
    <div>
        <v-container fluid>
            <div class="text-h5 text-sm-h5 text-md-h4 ma-3 text-center">
                Listado Traspasos Mercancía / Inmovilizado
            </div>

            <!-- ── FILTROS ─────────────────────────────────────────────────── -->
            <v-card class="pa-4 mb-4" elevation="2">
                <v-form ref="form" :disabled="loading">
                    <v-row align="center">

                        <!-- Ciudad -->
                        <v-col cols="12" sm="6" md="3" lg="2" class="mb-n4">
                            <v-select
                                v-model="filtros.CiudadId"
                                :items="ciudades"
                                item-text="Nombre"
                                item-value="CiudadId"
                                label="Ciudad"
                                outlined dense clearable hide-details
                                color="primary"
                                :loading="loadingCiudades"
                            ></v-select>
                        </v-col>

                        <!-- Fecha Desde -->
                        <v-col cols="6" sm="4" md="2" class="mb-n4">
                            <v-text-field
                                v-model="filtros.FechaDesde"
                                label="Fecha Desde"
                                type="date"
                                outlined dense hide-details color="primary"
                            ></v-text-field>
                        </v-col>

                        <!-- Fecha Hasta -->
                        <v-col cols="6" sm="4" md="2" class="mb-n4">
                            <v-text-field
                                v-model="filtros.FechaHasta"
                                label="Fecha Hasta"
                                type="date"
                                outlined dense hide-details color="primary"
                            ></v-text-field>
                        </v-col>

                        <!-- Tienda Desde -->
                        <v-col cols="6" sm="3" md="1" class="mb-n4">
                            <v-text-field
                                v-model.number="filtros.AlmacenDesde"
                                label="Tienda desde"
                                type="number" min="1"
                                outlined dense hide-details color="primary"
                            ></v-text-field>
                        </v-col>

                        <!-- Tienda Hasta -->
                        <v-col cols="6" sm="3" md="1" class="mb-n4">
                            <v-text-field
                                v-model.number="filtros.AlmacenHasta"
                                label="Tienda hasta"
                                type="number" min="1"
                                outlined dense hide-details color="primary"
                            ></v-text-field>
                        </v-col>

                        <!-- Tipo artículo: igual que opgfamiliacomision del FoxPro -->
                        <v-col cols="12" sm="auto" class="mb-n2">
                            <div class="text-caption grey--text mb-1">Tipo artículo</div>
                            <v-radio-group
                                v-model="filtros.Tipo"
                                row dense hide-details
                                class="mt-0 pt-0"
                            >
                                <v-radio label="Mercancía"    :value="1" color="primary"></v-radio>
                                <v-radio label="Inmovilizado" :value="2" color="primary"></v-radio>
                            </v-radio-group>
                        </v-col>

                    </v-row>

                    <!-- Botones -->
                    <v-row class="mt-4" align="center">
                        <v-col cols="auto">
                            <v-btn color="primary" :loading="loading"
                                   :disabled="!filtros.CiudadId" @click="calcular">
                                <v-icon left>mdi-calculator</v-icon>
                                Calcular
                            </v-btn>
                        </v-col>
                        <v-col cols="auto">
                            <v-btn color="primary" :disabled="!hayDatos" @click="imprimir">
                                <v-icon left>mdi-printer</v-icon>
                                Imprimir
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card>

            <!-- ── RESULTADO ───────────────────────────────────────────────── -->
            <v-card v-if="hayDatos" elevation="2">
                <v-card-title class="text-subtitle-1 font-weight-bold py-2 px-4">
                    {{ tituloTipo }} — {{ subtitulo }}
                </v-card-title>
                <v-card-text class="pa-0">
                    <div style="overflow-x: auto">
                        <table class="tabla-traspasos">
                            <thead>
                                <tr>
                                    <th class="col-tienda">Tienda</th>
                                    <th>Importe</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="l in lineas" :key="l.Codigo">
                                    <td class="col-tienda">
                                        {{ l.Codigo }} - {{ l.NombreRemoto }}
                                    </td>
                                    <td :class="{ 'importe-neg': l.Importe < 0 }">
                                        {{ fn(l.Importe) }}
                                    </td>
                                </tr>
                                <!-- Fila TOTAL — igual que el FRT -->
                                <tr class="fila-total">
                                    <td class="col-tienda">TOTAL</td>
                                    <td :class="{ 'importe-neg': total < 0 }">
                                        {{ fn(total) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </v-card-text>
            </v-card>

            <v-alert v-else-if="buscado" type="info" outlined class="mt-4">
                No hay datos para los filtros seleccionados.
            </v-alert>

        </v-container>
    </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

export default {
    name: "TraspasosMercancia",
    computed: {
        ...mapState(['urlRaiz']),

        hayDatos () { return this.lineas.length > 0; },

        tituloTipo () {
            return this.filtros.Tipo === 1
                ? 'TRASPASOS DE MERCANCÍA'
                : 'TRASPASOS DE INMOVILIZADO';
        },

        subtitulo () {
            if (!this.filtros.FechaDesde || !this.filtros.FechaHasta) return '';
            return `Del ${this.formatDate(this.filtros.FechaDesde)} al ${this.formatDate(this.filtros.FechaHasta)}`;
        },

        total () {
            return Math.round(
                this.lineas.reduce((acc, l) => acc + (l.Importe || 0), 0) * 100
            ) / 100;
        }
    },
    data () {
        return {
            loading:         false,
            loadingCiudades: false,
            buscado:         false,
            lineas:          [],
            ciudades:        [],

            filtros: {
                CiudadId:     null,
                FechaDesde:   this.primerDiaMesActual(),
                FechaHasta:   this.ultimoDiaMesActual(),
                AlmacenDesde: 1,
                AlmacenHasta: 99,
                Tipo:         1
            }
        };
    },
    mounted () {
        this.loadCiudades();
    },
    methods: {

        primerDiaMesActual () {
            const d = new Date();
            return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
        },
        ultimoDiaMesActual () {
            const d  = new Date();
            const ul = new Date(d.getFullYear(), d.getMonth()+1, 0);
            return `${ul.getFullYear()}-${String(ul.getMonth()+1).padStart(2,'0')}-${String(ul.getDate()).padStart(2,'0')}`;
        },

        // ── Ciudades ────────────────────────────────────────────────────────
        loadCiudades () {
            this.loadingCiudades = true;
            const AuthToken = localStorage.getItem('token');
            axios({ method: "POST", url: this.urlRaiz + "/api/mataux/ciudades",
                    headers: { "content-type": "application/json", "Authorization": AuthToken } })
                .then(r => {
                    this.ciudades = (r.data.Ciudades || []).map(c => ({
                        CiudadId: c.CiudadId ?? c.ciudadId,
                        Nombre:   c.Nombre   ?? c.nombre
                    }));
                })
                .catch(() => alert('Error al cargar las ciudades.'))
                .finally(() => { this.loadingCiudades = false; });
        },

        // ── Calcular ────────────────────────────────────────────────────────
        calcular () {
            if (!this.validarFiltros()) return;
            this.loading = true;
            this.buscado = false;
            const AuthToken = localStorage.getItem('token');
            axios({
                method: "POST",
                url:    this.urlRaiz + "/api/traspasos/calcular",
                data:   JSON.stringify(this.filtros),
                headers: { "content-type": "application/json", "Authorization": AuthToken }
            })
            .then(r => {
                this.lineas  = r.data.Lineas || [];
                this.buscado = true;
            })
            .catch(e => alert('Error al calcular: ' + (e.response?.data ?? e.message)))
            .finally(() => { this.loading = false; });
        },

        // ── Imprimir — similar al report FoxPro tpv_traspasos.frx ─────────────
        // Título:    "TRASPASOS DE MERCANCIA" / "TRASPASOS DE INMOVILIZADO"
        // Subtítulo: "Del {fecha} al {fecha}"
        // Columnas:  TIENDA | IMPORTE
        // Datos:     "codigo - nombre" | importe formato "999999.99"
        // Fila TOTAL al final
        imprimir () {
            const titulo   = this.tituloTipo;
            const subtit   = this.subtitulo;

            const fn = v => (v == null || isNaN(v))
                ? ''
                : parseFloat(v).toFixed(2);

            const filas = this.lineas.map(l => `
                <tr>
                    <td class="izq">${l.Codigo} - ${l.NombreRemoto}</td>
                    <td class="${l.Importe < 0 ? 'neg' : ''}">${fn(l.Importe)}</td>
                </tr>`).join('');

            const html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="UTF-8"><title>${titulo}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: Calibri, "Segoe UI", Arial, sans-serif;
    font-size: 8pt;
    color: #000;
    margin: 15mm 15mm 10mm 15mm;
  }
  .titulo   { font-size: 18pt; font-weight: bold; text-align: center; margin-bottom: 1mm; }
  .subtitulo{ font-size: 8pt; text-align: center; margin-bottom: 6mm; color: #444; }
  table { border-collapse: collapse; width: 60%; min-width: 300px; }
  th {
    background-color: #000;
    color: #fff;
    padding: 2px 4px;
    text-align: right;
    border: 1px solid #d3d3d3;
    font-weight: normal;
    font-size: 8pt;
  }
  th.izq { text-align: left; }
  td {
    padding: 2px 4px;
    border: 1px solid #d3d3d3;
    text-align: right;
    font-size: 8pt;
  }
  td.izq { text-align: left; }
  td.neg  { color: #cc0000; }
  tr.total td {
    font-weight: bold;
    border-top: 2px solid #000;
  }
  @media print {
    @page { size: A4 portrait; margin: 10mm; }
    body  { margin: 0; }
  }
</style></head><body>
<div class="titulo">${titulo}</div>
<div class="subtitulo">${subtit}</div>
<table>
  <thead>
    <tr>
      <th class="izq">TIENDA</th>
      <th>IMPORTE</th>
    </tr>
  </thead>
  <tbody>
    ${filas}
    <tr class="total">
      <td class="izq">TOTAL</td>
      <td class="${this.total < 0 ? 'neg' : ''}">${fn(this.total)}</td>
    </tr>
  </tbody>
</table>
</body></html>`;

            const w = window.open('', '_blank', 'width=700,height=800');
            w.document.write(html);
            w.document.close();
            w.focus();
            w.onafterprint = () => w.close();
            setTimeout(() => { w.print(); }, 400);
        },

        // ── Validaciones (igual que FoxPro) ─────────────────────────────────
        validarFiltros () {
            if (!this.filtros.CiudadId) {
                alert('Selecciona una ciudad.'); return false;
            }
            if (!this.filtros.FechaDesde || !this.filtros.FechaHasta) {
                alert('Indica el rango de fechas.'); return false;
            }
            if (this.filtros.FechaHasta < this.filtros.FechaDesde) {
                alert('La fecha final no puede ser menor que la fecha inicial.'); return false;
            }
            if (this.filtros.AlmacenHasta < this.filtros.AlmacenDesde) {
                alert('La tienda final no puede ser menor que la tienda inicial.'); return false;
            }
            return true;
        },

        // Formato importe: igual que "999999.99" del FRT
        fn (v) {
            if (v == null || isNaN(v)) return '';
            return parseFloat(v).toLocaleString('es-ES',
                { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        formatDate (v) {
            if (!v) return '';
            const [y, m, d] = v.split('-');
            return `${d}/${m}/${y}`;
        }
    }
};
</script>

<style>
.tabla-traspasos {
    border-collapse: collapse;
    width: 100%;
    max-width: 500px;
    font-size: 12px;
}
.tabla-traspasos th {
    background-color: #000;
    color: #fff;
    padding: 4px 8px;
    text-align: right;
    border: 1px solid #d3d3d3;
    font-weight: normal;
    white-space: nowrap;
}
.tabla-traspasos th.col-tienda { text-align: left; }
.tabla-traspasos td {
    padding: 3px 8px;
    border: 1px solid #d3d3d3;
    text-align: right;
    white-space: nowrap;
}
.tabla-traspasos td.col-tienda { text-align: left; min-width: 220px; }
.tabla-traspasos .importe-neg  { color: #cc0000; }
.tabla-traspasos .fila-total td {
    font-weight: bold;
    border-top: 2px solid #000;
    background-color: #e3f0fb;
}
</style>
